// pages/Fiche.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import NotificationPanel from '../components/NotificationPanel';
import DocumentGeneratorModal from '../components/DocumentGeneratorModal';
import DocumentViewer from '../components/DocumentViewer';
import { useEmployeeDetail } from '../hooks/useEmployeeDetail';
import { deleteEmployee, fetchDocumentTemplates, type DocumentTemplate } from '../api/employees';
import { getEmployeeState, getCddDuration, CDD_MAX_MONTHS } from '../utils/employeeStatus';
import { formatDateFr } from '../utils/date';


const Fiche: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const numericId = id ? Number(id) : undefined;
    const { employee, loading, error } = useEmployeeDetail(numericId);
    console.debug('[Fiche] Param id =', id, 'numericId =', numericId);
    console.debug('[Fiche] loading=', loading, 'error=', error, 'employee=', employee);

    // NOUVEAU: État pour contrôler l'ouverture/fermeture du panneau de notifications
    const [isNotifPanelOpen, setIsNotifPanelOpen] = useState(false);
    // NOUVEAU: État pour contrôler l'ouverture/fermeture du popup de suppression
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    // État pour gérer le chargement de la suppression
    const [isDeleting, setIsDeleting] = useState(false);
    // (supprimé) Modal documents (ancien système de génération)
    // État du modal Archives
    
    // États pour la génération de documents
    const [templates, setTemplates] = useState<Record<string, DocumentTemplate[]>>({});
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | null>(null);
    const [showDocModal, setShowDocModal] = useState(false);
    // Avenants déjà générés
    interface GeneratedDocument {
        filename: string;
        display_name: string;
        size: string;
        modified: string;
        url: string;
        category?: string | null;
        relative_path?: string;
    }
    const [_avenantsDocs, setAvenantsDocs] = useState<GeneratedDocument[]>([]);
    const [_loadingAvenants, setLoadingAvenants] = useState(false);

    // États pour la prévisualisation d'avenant avec DocumentPreviewModal
    const [previewState, setPreviewState] = useState({
        isOpen: false,
        documentUrl: '',
        documentName: '',
    });

    // Charger les templates au montage
    useEffect(() => {
        const loadTemplates = async () => {
            try {
                const data = await fetchDocumentTemplates();
                setTemplates(data.categories);
            } catch (error) {
                console.error('Erreur chargement templates:', error);
            } finally {
                setLoadingTemplates(false);
            }
        };
        loadTemplates();
    }, []);

    // Charger les avenants générés pour cet employé
    useEffect(() => {
        if (!employee?.id) return;
        const loadAvenants = async () => {
            try {
                setLoadingAvenants(true);
                const apiUrl = import.meta.env.VITE_API_URL || 'http://192.168.1.3:8000';
                const baseUrl = apiUrl.replace(/\/api\/?$/, '');
                const url = `${baseUrl}/api/employees/employees/${employee.id}/list_generated_documents/`;
                const resp = await fetch(url, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('access') || ''}`,
                    }
                });
                if (!resp.ok) {
                    console.error('[Fiche] Erreur chargement avenants', resp.status);
                    return;
                }
                const data = await resp.json();
                const docs: GeneratedDocument[] = (data.documents || []).filter((d: any) => {
                    const rel = (d.relative_path || '').toLowerCase();
                    const cat = (d.category || '').toLowerCase();
                    // Filtrer uniquement les avenants dans le dossier generated/avenants
                    // Le relative_path est de la forme "avenants/filename.docx"
                    return cat === 'avenants' || rel.startsWith('avenants/') || rel.startsWith('avenants\\');
                }).map((d: any) => ({
                    filename: d.filename,
                    display_name: d.display_name,
                    size: d.size,
                    modified: d.modified,
                    url: d.url,
                    category: d.category,
                    relative_path: d.relative_path,
                }));
                setAvenantsDocs(docs);
            } catch (e) {
                console.error('[Fiche] Exception loadAvenants', e);
            } finally {
                setLoadingAvenants(false);
            }
        };
        loadAvenants();
    }, [employee?.id]);

    // Fonctions désactivées temporairement - à réactiver quand l'UI avenants sera implémentée
    // const handleDownloadAvenant = (doc: GeneratedDocument) => {
    //     const a = document.createElement('a');
    //     a.href = doc.url;
    //     a.download = doc.filename;
    //     document.body.appendChild(a);
    //     a.click();
    //     document.body.removeChild(a);
    // };

    // const handlePreviewAvenant = async (doc: GeneratedDocument) => {
    //     try {
    //         const apiUrl = import.meta.env.VITE_API_URL || 'http://192.168.1.4:8000/api/';
    //         const baseUrl = apiUrl.replace(/\/api\/?$/, '');
    //         let documentUrl = doc.url;
    //         if (!documentUrl.startsWith('http')) {
    //             documentUrl = documentUrl.startsWith('/')
    //                 ? `${baseUrl}${documentUrl}`
    //                 : `${baseUrl}/${documentUrl}`;
    //         }
    //         const response = await fetch(documentUrl, {
    //             headers: {
    //                 'Authorization': `Bearer ${localStorage.getItem('access') || ''}`,
    //             }
    //         });
    //         if (!response.ok) {
    //             throw new Error('Impossible de charger le document');
    //         }
    //         const blob = await response.blob();
    //         const blobUrl = URL.createObjectURL(blob);
    //         setPreviewState({
    //             isOpen: true,
    //             documentUrl: blobUrl,
    //             documentName: doc.filename,
    //         });
    //     } catch (error) {
    //         console.error('Erreur prévisualisation avenant:', error);
    //         alert('Impossible de prévisualiser le document');
    //     }
    // };

    const closePreview = () => {
        // Nettoyer le blob URL si c'en est un
        if (previewState.documentUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewState.documentUrl);
        }

        setPreviewState({
            isOpen: false,
            documentUrl: '',
            documentName: '',
        });
    };

    // Archives: fichiers rattachés à l'employé (PDF/Image)
    // (supprimé) ancien calcul d'archives combinées

    // Navigation vers la page Archives
    const handleArchivesClick = () => {
        navigate(`/employees/${employee?.id}/archives`);
    };

    // Gestion du chargement et des erreurs
    if (loading) {
        console.debug('[Fiche] État: loading');
        return (
            <div className={`flex h-screen bg-base-200 ${isNotifPanelOpen ? 'overflow-hidden' : ''}`}>
                <Sidebar />
                <div className="flex-1 flex flex-col p-6 overflow-auto">
                    <Navbar onOpenNotifPanel={() => setIsNotifPanelOpen(true)} />
                    <div className="flex justify-center items-center h-full">
                        <span className="loading loading-spinner loading-lg"></span>
                    </div>
                </div>
                <NotificationPanel 
                    isOpen={isNotifPanelOpen} 
                    onClose={() => setIsNotifPanelOpen(false)} 
                />
            </div>
        );
    }

    if (error || !employee) {
        console.warn('[Fiche] Alerte: error=', error, 'employee=', employee);
        return (
            <div className={`flex h-screen bg-base-200 ${isNotifPanelOpen ? 'overflow-hidden' : ''}`}>
                <Sidebar />
                <div className="flex-1 flex flex-col p-6 overflow-auto">
                    <Navbar onOpenNotifPanel={() => setIsNotifPanelOpen(true)} />
                    <div className="flex justify-center items-center h-full">
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-error">
                                {error || 'Employé non trouvé'}
                            </h2>
                            <button 
                                className="btn btn-warning mt-4"
                                onClick={() => navigate('/employees')}
                            >
                                Retour à la liste
                            </button>
                        </div>
                    </div>
                </div>
                
                {/* Panneau de Notifications */}
                <NotificationPanel 
                    isOpen={isNotifPanelOpen} 
                    onClose={() => setIsNotifPanelOpen(false)} 
                />
            </div>
        );
    }

    const handleBack = () => {
        navigate('/employees');
    };

    // (supprimé) handleDocumentClick pour ancien modal de génération de documents

    // Dans Fiche.tsx - modifier la fonction handleAvenantClick
const handleAvenantClick = () => {
    navigate(`/employees/${employee.id}/avenant`);
};

// (supprimé) Ancienne variante d'ouverture du modal Archives avec un autre état

    // NOUVEAU: Fonctions pour gérer la suppression du profil
    const handleDeleteProfile = () => {
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!employee) return;
        
        try {
            setIsDeleting(true);
            await deleteEmployee(employee.id);
            // Redirection après suppression réussie
            navigate('/employees', { 
                replace: true,
                state: { message: `L'employé ${employee.full_name} a été supprimé avec succès` }
            });
        } catch (err) {
            console.error('Erreur lors de la suppression:', err);
            alert('Erreur lors de la suppression de l\'employé. Veuillez réessayer.');
            setIsDeleting(false);
            setIsDeleteModalOpen(false);
        }
    };

    const handleCancelDelete = () => {
        setIsDeleteModalOpen(false);
    };

    const handleEditEmployee = () => {
        // Rediriger vers la page de modification (à créer)
        navigate(`/employees/${employee?.id}/edit`);
    };

    // Filtrer les templates visibles selon le sexe de l'employé
    const isTemplateAllowedForGender = (tpl: DocumentTemplate, gender?: string | null) => {
        const label = (tpl.display_name || tpl.filename || '').toUpperCase();
        const isHomme = label.includes('HOMME');
        const isFemme = label.includes('FEMME');
        const isMaternite = label.includes('MATERNITE') || label.includes('MATERNITÉ');
        const isDomiciliation = label.includes('DOMICILIATION');
        const isRepriseService = label.includes('REPRISE');
        const isPromotrices = label.includes('PROMOTRICES');
        const isSoldeToutCompte = label.includes('SOLDE');
        const isNotificationFinContrat = label.includes('NOTIFICATION');
        const isAvenant = label.includes('AVENANT');
        
        // 1. Templates toujours visibles (neutres)
        if (isDomiciliation || isRepriseService || isSoldeToutCompte || isNotificationFinContrat || isAvenant) return true;

        // 2. Si le genre n'est pas défini, on affiche tout par précaution (ou on pourrait masquer les spécifiques)
        // Pour éviter de bloquer l'utilisateur si l'import Excel n'a pas mis le genre
        if (!gender) return true;
        
        // 3. Templates spécifiques aux hommes
        if (isHomme) return gender === 'M';
        
        // 4. Templates spécifiques aux femmes (incluant maternité et promotrices)
        if (isFemme || isMaternite || isPromotrices) return gender === 'F';
        
        // 5. Par défaut, si le template n'a pas de mot-clé spécifique, on l'affiche
        return true;
    };

    // Générer initiales pour l'avatar
    const getInitials = (name: string) => {
        const parts = name.trim().split(' ').filter(Boolean);
        if (parts.length === 0) return '?';
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    // Générer couleur de fond basée sur l'ID
    const getAvatarColor = (id: number) => {
        const colors = [
            'bg-gradient-to-br from-purple-500 to-pink-500',
            'bg-gradient-to-br from-blue-500 to-cyan-500',
            'bg-gradient-to-br from-green-500 to-teal-500',
            'bg-gradient-to-br from-orange-500 to-red-500',
            'bg-gradient-to-br from-indigo-500 to-purple-500',
            'bg-gradient-to-br from-yellow-500 to-orange-500',
        ];
        return colors[id % colors.length];
    };

    return (
        <div className={`flex h-screen bg-base-200 ${isNotifPanelOpen ? 'overflow-hidden' : ''}`}>
            <Sidebar />
            
            <div className="flex-1 flex flex-col p-6 overflow-auto">
                {/* Navbar avec gestion des notifications */}
                <Navbar onOpenNotifPanel={() => setIsNotifPanelOpen(true)} />
                
                <div className="flex flex-col gap-6">
                    {/* En-tête avec bouton retour */}
                    <div className="flex justify-between items-center">
                        <div></div>
                        <h2 className="text-3xl font-extrabold text-base-content">
                            Fiche Employé
                        </h2>
                        <div className="flex gap-2">
                            <button 
                                className="btn btn-warning"
                                onClick={handleBack}
                            >
                                ← Retour
                            </button>
                        </div>
                    </div>

                    {/* Carte principale de la fiche */}
                    <div className="card bg-base-100 shadow-xl">
                        <div className="card-body">
                            {/* En-tête visuel avec photo + infos clés */}
                            <div className="flex items-center gap-5 mb-6">
                                <div className="avatar placeholder group relative">
                                    <div className={`${employee.photo ? 'bg-neutral-focus' : getAvatarColor(employee.id)} text-white rounded-full w-24 h-24 overflow-hidden flex items-center justify-center`}>
                                        {employee.photo ? (
                                            <img src={employee.photo as unknown as string} alt={employee.full_name} className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-3xl font-bold">{getInitials(employee.full_name)}</span>
                                        )}
                                    </div>
                                    {/* Bouton changer photo au survol */}
                                    <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                                         onClick={() => navigate(`/employees/${employee.id}/edit`)}>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-2xl font-bold text-warning leading-tight">{employee.full_name}</h3>
                                    <p className="text-sm text-base-content/70 font-mono">Matricule: {employee.employee_id || 'Non attribué'}</p>
                                    <div>
                                        {(() => {
                                            const etat = getEmployeeState(employee);
                                            return <span className={`badge ${etat.badgeClass}`}>{etat.label}</span>;
                                        })()}
                                    </div>
                                </div>
                            </div>

                            {/* Plafond légal des CDD : visible dès la fiche */}
                            {(() => {
                                const cdd = getCddDuration(employee);
                                if (!cdd.isCdd || (!cdd.limitReached && !cdd.approaching)) return null;
                                return (
                                    <div className={`alert ${cdd.limitReached ? 'alert-error' : 'alert-warning'} mb-4 py-2`}>
                                        <span className="text-sm">
                                            {cdd.limitReached ? '⛔' : '⚠️'} CDD de <strong>{cdd.months} mois</strong> —
                                            {cdd.limitReached
                                                ? ` plafond légal de ${CDD_MAX_MONTHS} mois atteint, ne pas proroger.`
                                                : ` plafond de ${CDD_MAX_MONTHS} mois bientôt atteint.`}
                                        </span>
                                    </div>
                                );
                            })()}

                            {/* Section Informations Personnelles avec boutons Avenant et Archives */}
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-xl font-bold text-warning">Informations Personnelles</h3>
                                <div className="flex gap-2">
                                    <button 
                                        className="btn btn-primary"
                                        onClick={handleAvenantClick}
                                    >
                                        Avenant
                                    </button>
                                    {/* Bouton Archives - Couleurs logo Fidex */}
                                    <button 
                                        className="btn bg-[#FDB913] hover:bg-[#E5A511] text-black border-none shadow-md"
                                        onClick={handleArchivesClick}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                        </svg>
                                        Archives
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                                <div className="space-y-2">
                                    <p><strong>NOM:</strong> {employee.last_name}</p>
                                    <p><strong>PRENOMS:</strong> {employee.first_name}</p>
                                    <p><strong>SEXE:</strong> {employee.gender === 'M' ? 'Masculin' : employee.gender === 'F' ? 'Féminin' : 'Non renseigné'}</p>
                                    <p><strong>NE LE:</strong> {formatDateFr(employee.date_of_birth) || 'Non renseigné'}</p>
                                    <p><strong>A:</strong> {employee.place_of_birth || 'Non renseigné'}</p>
                                    <p><strong>PERE:</strong> {employee.father_name || 'Non renseigné'}</p>
                                    <p><strong>MERE:</strong> {employee.mother_name || 'Non renseigné'}</p>
                                </div>
                                
                                <div className="space-y-2">
                                    <p><strong>NATURE PIECE IDENTITE:</strong> {employee.identity_type || 'Non renseigné'}</p>
                                    <p><strong>N° PIECE IDENTITE:</strong> {employee.identity_number || 'Non renseigné'}</p>
                                    <p><strong>DATE D'ÉTABLISSEMENT:</strong> {formatDateFr((employee as any).identity_issue_date) || 'Non renseigné'}</p>
                                    <p><strong>VALIDE JUSQU'AU:</strong> {formatDateFr(employee.identity_valid_until) || 'Non renseigné'}</p>
                                    <p><strong>NATIONALITE:</strong> {employee.nationality || 'Non renseigné'}</p>
                                    <p><strong>DOMICILE:</strong> {employee.address || 'Non renseigné'}</p>
                                </div>
                                
                                <div className="space-y-2">
                                    <p><strong>CONTACTS:</strong> {employee.phone || 'Non renseigné'}</p>
                                    <p><strong>E-MAIL:</strong> {employee.email || 'Non renseigné'}</p>
                                    <p><strong>N°CNPS:</strong> {employee.cnps_number || 'Non renseigné'}</p>
                                </div>
                            </div>

                            {/* Section Informations Professionnelles */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                <div className="col-span-full border-b border-base-300 pb-4 mb-4">
                                    <h3 className="text-xl font-bold text-warning">Informations Professionnelles</h3>
                                </div>
                                
                                <div className="space-y-2">
                                    <p><strong>AFFECTE AU DEPARTEMENT:</strong> {employee.department_name}</p>
                                    <p><strong>SOCIETE D'INSTRUCTION:</strong> {employee.company_name}</p>
                                    <p><strong>TYPE DE CONTRAT:</strong> {employee.contract_type || 'Non renseigné'}</p>
                                    <p><strong>DATE DE SIGNATURE:</strong> {formatDateFr(employee.contract_signature_date) || 'Non renseigné'}</p>
                                    <p><strong>DEBUT:</strong> {formatDateFr(employee.contract_start_date) || 'Non renseigné'}</p>
                                    <p><strong>FIN:</strong> {formatDateFr(employee.contract_end_date) || "Non définie"}</p>
                                </div>
                                
                                <div className="space-y-2">
                                    <p><strong>FONCTION:</strong> {employee.position_display}</p>
                                    <p><strong>CATEGORIE PROFESSIONNELLE:</strong> {employee.professional_category || 'Non renseigné'}</p>
                                    <p><strong>LIEU DE TRAVAIL:</strong> {employee.work_location || 'Non renseigné'}</p>
                                </div>
                            </div>

                            {/* Section Rémunération */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="col-span-full border-b border-base-300 pb-4 mb-4">
                                    <h3 className="text-xl font-bold text-warning">Rémunération</h3>
                                </div>
                                
                                <div className="space-y-2">
                                    <p><strong>SALAIRE BRUT:</strong> {employee.base_salary?.toLocaleString() || 'Non renseigné'} FCFA</p>
                                    <p><strong>SUR-SALAIRE:</strong> {employee.sur_salaire?.toLocaleString() || 'Non renseigné'} FCFA</p>
                                    <p><strong>SALAIRE CATEGORIEL:</strong> {employee.salaire_categoriel?.toLocaleString() || 'Non renseigné'} FCFA</p>
                                    <p><strong>PRIME DE TRANSPORT:</strong> {employee.transport_allowance?.toLocaleString() || 'Non renseigné'} FCFA</p>
                                    <p><strong>PRIME DE RENDEMENT:</strong> {employee.performance_bonus?.toLocaleString() || 'Non renseigné'} FCFA</p>
                                    <p><strong>PRIME DE COMMUNICATION:</strong> {employee.communication_allowance?.toLocaleString() || 'Non renseigné'} FCFA</p>
                                </div>
                                
                                {/* Suppression de l'affichage 'TOTAL SALAIRE NET MENSUEL' selon demande */}
                            </div>

                            {/* NOUVEAU: Boutons Modifier et Supprimer profil */}
                            <div className="mt-6 flex justify-center gap-3">
                                <button 
                                    className="btn btn-primary"
                                    onClick={handleEditEmployee}
                                >
                                    ✏️ Modifier
                                </button>
                                <button 
                                    className="btn btn-error text-white"
                                    onClick={handleDeleteProfile}
                                >
                                    Supprimer profil
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Section Génération de Documents */}
                    <div className="card bg-white shadow-2xl border border-gray-100 mt-6">
                        <div className="card-body">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-3 rounded-xl">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                                        Génération de Documents
                                    </h3>
                                    <p className="text-gray-600">Cliquez sur un document pour le générer automatiquement</p>
                                </div>
                            </div>

                            {loadingTemplates ? (
                                <div className="flex justify-center py-12">
                                    <span className="loading loading-spinner loading-lg text-primary"></span>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {Object.entries(templates).map(([category, docs]) => {
                                        const filtered = docs.filter(t => isTemplateAllowedForGender(t, employee.gender));
                                        if (filtered.length === 0) return null;
                                        return (
                                            <div key={category} className="border border-gray-200 rounded-xl overflow-hidden">
                                                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-3 border-b border-gray-200">
                                                    <h4 className="font-bold text-gray-800 flex items-center gap-2">
                                                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                                        {category}
                                                        <span className="badge badge-sm bg-blue-100 text-blue-700 border-0 ml-2">
                                                            {filtered.length}
                                                        </span>
                                                    </h4>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
                                                    {filtered.map((template) => (
                                                        <button
                                                            key={template.filename}
                                                            onClick={() => {
                                                                setSelectedTemplate(template);
                                                                setShowDocModal(true);
                                                            }}
                                                            className="group relative bg-white hover:bg-gradient-to-br hover:from-blue-50 hover:to-purple-50 border-2 border-gray-200 hover:border-blue-400 rounded-xl p-4 text-left transition-all duration-200 hover:shadow-lg hover:-translate-y-1"
                                                        >
                                                            <div className="flex items-start gap-3">
                                                                <div className="bg-blue-100 group-hover:bg-blue-500 p-2 rounded-lg transition-colors">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 group-hover:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                                    </svg>
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <h5 className="font-semibold text-gray-800 group-hover:text-blue-700 text-sm mb-1 line-clamp-2">
                                                                        {template.display_name}
                                                                    </h5>
                                                                    <span className="text-xs text-gray-500 group-hover:text-gray-700">
                                                                        Cliquez pour générer
                                                                    </span>
                                                                </div>
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                                </svg>
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {Object.keys(templates).length === 0 && (
                                        <div className="text-center py-12 text-gray-500">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            <p>Aucun template disponible</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* -------------------- Panneau de Notifications (Composant Modulaire) -------------------- */}
            <NotificationPanel 
                isOpen={isNotifPanelOpen} 
                onClose={() => setIsNotifPanelOpen(false)} 
            />

            {/* Popup de confirmation de suppression */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-base-100 p-6 rounded-lg shadow-xl max-w-md">
                        <h3 className="text-xl font-bold text-error mb-4">Attention</h3>
                        <p className="mb-6">
                            Êtes-vous sûr de vouloir supprimer le profil de {employee.full_name} ? 
                            Cette action est irréversible.
                        </p>
                        <div className="flex justify-end gap-4">
                            <button 
                                className="btn btn-outline"
                                onClick={handleCancelDelete}
                                disabled={isDeleting}
                            >
                                NON
                            </button>
                            <button 
                                className="btn btn-error text-white"
                                onClick={handleConfirmDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <span className="loading loading-spinner loading-sm"></span>
                                        Suppression...
                                    </>
                                ) : (
                                    'OUI'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de génération de documents */}
            {showDocModal && selectedTemplate && employee && (
                <DocumentGeneratorModal
                    isOpen={showDocModal}
                    onClose={() => {
                        setShowDocModal(false);
                        setSelectedTemplate(null);
                    }}
                    template={selectedTemplate}
                    employeeId={employee.id}
                    employeeName={`${employee.first_name} ${employee.last_name}`}
                />
            )}

            {/* Prévisualisation Avenant avec DocumentViewer */}
            {previewState.isOpen && previewState.documentUrl && (
                <DocumentViewer
                    fileUrl={previewState.documentUrl}
                    fileName={previewState.documentName}
                    onClose={closePreview}
                />
            )}
        </div>
    );
};

export default Fiche;

// Modal aperçu avenant (injection juste avant export si besoin) - Ajout